import React, { useRef, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { fadeInFromNone } from "../../lib/animations";
import Row from "../Row";
import Col from "../Col";
import Gap from "../Gap";
import { TextComponent } from "../Text";
import { up } from "../../lib/styles";

// import passengerVideoSrc from "../../data/videos/uses/zuri-hybrid-vtol-passenger-mobility-scenario-new-york-city-to-nantucket.mp4";
// import hemsSarVideoSrc from "../../data/videos/uses/zuri-hybrid-vtol-medical-evacuation-scenario-riyadh-to-abha.mp4";
// import defenseVideoSrc from "../../data/videos/uses/zuri-hybrid-vtol-defense-security-mobility-scenario-sendai-to-hachinohe.mp4";
// import cargoVideoSrc from "../../data/videos/uses/zuri-hybrid-vtol-cargo-logistics-scenario-stuttgart-to-brno.mp4";

const BLUE = "#5bcadf"; // ZURI
const RED = "#e68481"; // plane
const GRAY = "#bdbdbd"; // CAR
const ORANGE = "#f88e29"; // helicopter

const ICON_PATH_BASE = "/uses-icons/";
const ZURI_ICON_PATH = `${ICON_PATH_BASE}zuri-hybrid-vtol-aircraft-icon.svg`;
const ZURI_ICON_ALT = "Zuri Hybrid VTOL aircraft comparison mode";
const AIRLINER_ICON_PATH = `${ICON_PATH_BASE}fixed-wing-airliner-comparison-icon.svg`;
const AIRLINER_ICON_ALT = "Commercial airline travel comparison";
const ROAD_ICON_PATH = `${ICON_PATH_BASE}road-vehicle-ground-transport-comparison-icon.svg`;
const ROAD_ICON_ALT = "Road travel time comparison";
const HELICOPTER_ICON_PATH = `${ICON_PATH_BASE}helicopter-rotary-wing-comparison-icon.svg`;
const HELICOPTER_ICON_ALT = "Helicopter evacuation comparison";

const zuriIconConfig = {
  path: ZURI_ICON_PATH,
  alt: ZURI_ICON_ALT,
};
const airlinerIcon = {
  path: AIRLINER_ICON_PATH,
  alt: AIRLINER_ICON_ALT,
};
const roadIconConfig = {
  path: ROAD_ICON_PATH,
  alt: ROAD_ICON_ALT,
};
const helicopterIconConfig = {
  path: HELICOPTER_ICON_PATH,
  alt: HELICOPTER_ICON_ALT,
};
/**
 * Generic uses video component.
 * Simple wrapper for raw <video>, can be reused if needed.
 */
const UsesVideo = ({ src, type = "video/mp4", ...props }) => (
  <video src={src} type={type} muted playsInline {...props} />
);

// Delay between individual vehicle items in the info animation (seconds)
const VEHICLES_ANIMATION_DELAY = 1.5;

const MAIN_BP = 1380;

// Config for the passenger use-case info block
const PassengerConfig = {
  title: "Passenger",
  videoUrl:
    "/uses/zuri-hybrid-vtol-passenger-mobility-scenario-new-york-city-to-nantucket.mp4",
  name:
    "Zuri Hybrid VTOL Passenger Mobility Scenario - New York City to Nantucket",
  placeholderImage:
    "/uses/zuri-hybrid-vtol-passenger-route-new-york-city-to-nantucket-regional-air-mobility.jpg",
  videoDescription:
    "Illustrative regional passenger travel scenario using a Zuri Hybrid VTOL between New York City and Nantucket showing direct point-to-point access without airport hubs or ferry transfers.",
  animationDelay: 3, // seconds, delay before info animation starts
  iconSrc: `${ICON_PATH_BASE}zuri-passenger-capacity-5-seat-icon.svg`,
  iconAlt: "Four passenger plus pilot seating layout",
  videoCropPosition: "50%",
  vehicles: [
    {
      title: "Zuri",
      dotColor: BLUE,
      time: "1 h 45 min",
      iconConfig: zuriIconConfig,
    },
    {
      title: "Airliner",
      dotColor: RED,
      time: "4 h 30 min",
      iconConfig: airlinerIcon,
    },
    {
      title: "Road",
      dotColor: GRAY,
      time: "6 h",
      iconConfig: roadIconConfig,
    },
  ],
};

const CargoConfig = {
  title: "Cargo",
  videoUrl:
    "/uses/zuri-hybrid-vtol-cargo-logistics-scenario-stuttgart-to-brno.mp4",
  placeholderImage:
    "/uses/zuri-hybrid-vtol-cargo-route-brno-to-stuttgart-cross-border-logistics.jpg",
  name: "Zuri Hybrid VTOL Cargo Logistics Scenario - Brno to Stuttgart",
  videoDescription:
    "Example cross-border cargo logistics scenario using a Zuri Hybrid VTOL between Brno and Stuttgart representing factory-to-factory delivery without runway infrastructure.",
  animationDelay: 3, // seconds, delay before info animation starts
  iconSrc: `${ICON_PATH_BASE}cargo-logistics-transport-mission-icon.svg`,
  iconAlt: "Cargo delivery mission type",
  videoCropPosition: "60%",
  vehicles: [
    {
      title: "Zuri",
      dotColor: BLUE,
      time: "2 h 05 min",
      iconConfig: zuriIconConfig,
    },
    {
      title: "Airliner",
      dotColor: RED,
      time: "Not viable",
      iconConfig: airlinerIcon,
    },
    {
      title: "Road",
      dotColor: GRAY,
      time: "8 h",
      iconConfig: roadIconConfig,
    },
  ],
};

const SARConfig = {
  title: "HEMS/SAR",
  name: "Zuri Hybrid VTOL Medical Evacuation Scenario - Riyadh to Abha",
  videoUrl:
    "/uses/zuri-hybrid-vtol-medical-evacuation-scenario-riyadh-to-abha.mp4",
  placeholderImage:
    "/uses/zuri-hybrid-vtol-medical-evacuation-route-riyadh-to-abha-hems-sar.jpg",
  videoDescription:
    "Representative medical evacuation scenario with a Zuri Hybrid VTOL between Riyadh and Abha demonstrating long-range emergency response beyond road and helicopter reach.",
  animationDelay: 3, // seconds, delay before info animation starts
  iconSrc: `${ICON_PATH_BASE}search-and-rescue-mission-icon.svg`,
  iconAlt: "Search and rescue mission type",
  videoCropPosition: "37%",
  vehicles: [
    {
      title: "Zuri",
      dotColor: BLUE,
      time: "3 h 25 min",
      iconConfig: zuriIconConfig,
    },
    {
      title: "Airliner",
      dotColor: RED,
      time: "5 h 15 min",
      iconConfig: airlinerIcon,
    },
    {
      title: "Helicopter",
      dotColor: ORANGE,
      time: "5 h 50 min",
      iconConfig: helicopterIconConfig,
    },
    {
      title: "Road",
      dotColor: GRAY,
      time: "8 h",
      iconConfig: roadIconConfig,
    },
  ],
};

const DefenseConfig = {
  title: "Defense",
  name:
    "Zuri Hybrid VTOL Defense and Security Mobility Scenario - Sendai to Hachinohe",
  videoUrl:
    "/uses/zuri-hybrid-vtol-defense-security-mobility-scenario-sendai-to-hachinohe.mp4",
  placeholderImage:
    "/uses/zuri-hybrid-vtol-defense-security-route-sendai-to-hachinohe-runway-independent-mobility.jpg",
  videoDescription:
    "Conceptual defense and disaster-response mobility scenario using a Zuri Hybrid VTOL between Sendai and Hachinohe supporting runway-independent readiness and relief operations.",
  animationDelay: 3, // seconds, delay before info animation starts
  iconSrc: `${ICON_PATH_BASE}defense-security-mission-icon.svg`,
  iconAlt: "Defense and security mobility mission",
  videoCropPosition: "57%",
  vehicles: [
    {
      title: "Zuri",
      dotColor: BLUE,
      time: "3 h 05 min",
      iconConfig: zuriIconConfig,
    },
    {
      title: "Airliner",
      dotColor: RED,
      time: "5 h 20 min",
      iconConfig: airlinerIcon,
    },
    {
      title: "Road",
      dotColor: GRAY,
      time: "8 h",
      iconConfig: roadIconConfig,
    },
  ],
};

const Circle = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${({ color }) => color};

  ${up(MAIN_BP)} {
    width: 20px;
    height: 20px;
  }
`;

const BlockWrapper = styled.div`
  background: #000000;
  color: #ffffff;
  padding: 0;
`;

const BlockContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  ${up("desktop")} {
    flex-direction: row;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  min-height: unset;
  flex-grow: 1;
  margin-bottom: 0;

  margin-left: -13%;
  width: 126%;
  max-width: 1200px;

  ${up("desktop")} {
    margin-left: 0;
    width: unset;
  }

  @media (min-width: 768px) {
    margin-bottom: 0;
    min-height: 220px;
  }
`;

const UsesInlineVideo = styled.video`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  object-fit: cover;
  display: block;
`;

const InfoContainer = styled.div`
  /* flex: 1 1 50%; */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 8px 24px 24px 24px;
  max-width: 800px;

  ${up("desktop")} {
    padding: 4px 24px 20px 24px;
  }

  ${up(MAIN_BP)} {
    padding: 20px 32px 20px 48px;
  }
`;

const InfoTitle = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 28px;
  ${up(MAIN_BP)} {
    font-size: 42px;
  }
`;

const VehiclesWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const VehiclesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  ${up("desktop")} {
    flex-direction: column;
  }
`;

const VehicleItem = styled.div`
  /* Hidden by default */
  opacity: 0;
  display: flex;
  flex-direction: row;
  align-items: center;

  ${({ active, delay }) =>
    active &&
    css`
      animation-name: ${fadeInFromNone};
      animation-duration: 0.6s;
      animation-fill-mode: forwards;
      animation-delay: ${delay};
    `}
`;

const VehicleTitle = styled.span`
  margin-right: 8px;
  margin-left: 0;
  font-size: 18px;
  color: #dadada;

  ${up("mobile")} {
    font-size: 22px;
  }
  ${up("desktop")} {
    font-size: 20px;
  }
  ${up(MAIN_BP)} {
    font-size: 24px;
  }
`;

const VehicleTime = styled.span`
  font-size: 20px;
  color: #ffffff;
  font-weight: 600;
  white-space: nowrap;

  ${up("mobile")} {
    font-size: 24px;
  }
  ${up("desktop")} {
    font-size: 22px;
  }

  ${up(MAIN_BP)} {
    font-size: 26px;
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  img {
    width: 100%;
    height: 100%;
  }

  ${up(MAIN_BP)} {
    width: 100px;
    height: 100px;
  }
`;

const VehicleIconWrapper = styled.div`
  width: 80px;
  height: auto;
  margin: 0 12px;
  ${up(374)} {
    width: 100px;
  }
  ${up("mobile")} {
    width: 110px;
    margin: 0 40px 0 32px;
  }
  ${up("desktop")} {
    width: 110px;
    margin: 0 24px 0 16px;
  }

  ${up(MAIN_BP)} {
    width: 160px;
    margin: 0 40px 0 32px;
  }

  img {
    width: 100%;
    height: 100%;
  }
`;

const VideoTrigger = styled.div`
  position: absolute;
  height: 100%;
  width: 10px;
  top: 0;
  left: 50%;
`;

/**
 * Composite block: video + info on black background.
 * - Video auto-plays (muted) when in view
 * - Info vehicles fade in with staggered delay based on config
 */
const UsesVideoWithInfo = ({ config, iconSrc, iconAlt, children }) => {
  const {
    videoUrl,
    videoDescription = "",
    videoCropPosition = "center",
    placeholderImage = "",
  } = config;
  const videoRef = useRef(null);
  const videoTriggerRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      try {
        // skip if video is already playing
        if (isActive || video.currentTime > 0) return;
        // show first frame instead of poster when loaded
        video.currentTime = 0;
      } catch {
        // ignore errors from setting currentTime on some browsers
      }
    };

    video.addEventListener("loadeddata", handleLoadedData);

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
    };
  }, [isActive]);

  useEffect(() => {
    if (typeof window === "undefined" || !videoTriggerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Trigger only once – when it first comes into view
        if (!entry.isIntersecting || hasAnimated) return;

        setIsActive(true);
        setHasAnimated(true);
      },
      {
        threshold: 1,
      },
    );

    observer.observe(videoTriggerRef.current);

    return () => {
      if (videoTriggerRef.current) {
        observer.unobserve(videoTriggerRef.current);
      }
      observer.disconnect();
    };
  }, [hasAnimated]);

  // Secondary observer: adjust play/pause based on visibility percentage.
  useEffect(() => {
    if (typeof window === "undefined" || !videoRef.current) return;

    const target = videoRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!hasAnimated) return;
        const ratio = entry.intersectionRatio || 0;

        // Pause when more than 25% of the video is out of view (visible <= 75%)
        if (isActive && ratio <= 0.75) {
          setIsActive(false);
        }

        // Resume when at least 70% of the video is visible
        if (!isActive && ratio >= 0.7) {
          setIsActive(true);
        }
      },
      {
        threshold: [0, 0.7, 0.75, 1],
      },
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
      observer.disconnect();
    };
  }, [isActive, hasAnimated]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      video.play().catch(() => {
        // autoplay can fail silently in some browsers; ignore
      });
    }

    if (hasAnimated && !isActive) {
      video.pause();
    }
  }, [isActive]);

  const baseDelay =
    config && typeof config.animationDelay === "number"
      ? config.animationDelay
      : 0;

  return (
    <>
      <BlockWrapper>
        <BlockContent>
          <VideoContainer>
            <VideoTrigger ref={videoTriggerRef} />
            <UsesInlineVideo
              ref={videoRef}
              style={{ objectPosition: videoCropPosition }}
              muted
              playsInline
              aria-label={videoDescription}
              preload="metadata"
              poster={placeholderImage}
              loop
              // no controls, no click handler – user cannot control video
            >
              <source src={videoUrl} type="video/mp4" />
              {videoDescription}
            </UsesInlineVideo>
          </VideoContainer>

          <InfoContainer>
            {config?.title && (
              <Row alignItems="center">
                <InfoTitle>{config.title}</InfoTitle>
                <IconWrapper>
                  <img src={config.iconSrc} alt={config.iconAlt} />
                </IconWrapper>
              </Row>
            )}
            {children}
            <Gap gap="32px" mobileGap="24px" />
            {config?.vehicles && (
              <VehiclesWrapper>
                <VehiclesList>
                  {config.vehicles.map((vehicle, index) => {
                    const totalDelaySeconds =
                      baseDelay + index * VEHICLES_ANIMATION_DELAY;
                    const delayString = `${totalDelaySeconds}s`;

                    return (
                      <VehicleItem
                        key={vehicle.title}
                        active={hasAnimated}
                        delay={delayString}
                      >
                        <Circle color={vehicle.dotColor} />
                        <VehicleIconWrapper>
                          <img
                            src={vehicle.iconConfig.path}
                            alt={vehicle.iconConfig.alt}
                          />
                        </VehicleIconWrapper>
                        <VehicleTitle>{vehicle.title}:</VehicleTitle>
                        <VehicleTime>{vehicle.time}</VehicleTime>
                      </VehicleItem>
                    );
                  })}
                </VehiclesList>
              </VehiclesWrapper>
            )}
          </InfoContainer>
        </BlockContent>
      </BlockWrapper>
      <div
        style={{
          margin: "auto",
          fontSize: "16px",
          color: "#8d8d8f",
          paddingTop: "8px",
          textAlign: "center",
          backgroundColor: "#FFFFFF",
          width: "100%",
        }}
      >
        Routes shown are representative capability scenarios, not scheduled
        services.
      </div>
    </>
  );
};

// export const PassengerUsesVideo = (props) => (
//   <UsesVideo src={passengerVideoSrc} {...props} />
// );

// export const HemsSarUsesVideo = (props) => (
//   <UsesVideo src={hemsSarVideoSrc} {...props} />
// );

// export const DefenseUsesVideo = (props) => (
//   <UsesVideo src={defenseVideoSrc} {...props} />
// );

// export const CargoUsesVideo = (props) => (
//   <UsesVideo src={cargoVideoSrc} {...props} />
// );

const BodyText = ({ ...props }) => (
  <TextComponent
    color="#dadada"
    fontSize="24px"
    mobileFontSize="20px"
    bp={MAIN_BP}
    {...props}
  />
);

// Block variant with info panel for the passenger use-case
export const PassengerUsesBlock = (props) => (
  <UsesVideoWithInfo config={PassengerConfig} {...props}>
    <BodyText>
      Avoid Hubs and Ferries on Short-to-Medium Regional Routes
      <br />
      (Passenger — NYC → Nantucket)
    </BodyText>
  </UsesVideoWithInfo>
);

export const CargoUsesBlock = (props) => (
  <UsesVideoWithInfo config={CargoConfig} {...props}>
    <BodyText>
      Time-Critical Logistics Without Runway Dependence
      <br />
      (Cargo — Brno → Stuttgart)
    </BodyText>
  </UsesVideoWithInfo>
);

export const SARUsesBlock = (props) => (
  <UsesVideoWithInfo config={SARConfig} {...props}>
    <BodyText>
      Medical Evacuation Beyond Road and Helicopter Reach
      <br />
      (Emergency — Riyadh → Abha)
    </BodyText>
  </UsesVideoWithInfo>
);

export const DefenseUsesBlock = (props) => (
  <UsesVideoWithInfo config={DefenseConfig} {...props}>
    <BodyText>
      Runway-Independent Mobility for Readiness and Response
      <br /> (Defense & Security — Sendai → Hachinohe)
    </BodyText>
  </UsesVideoWithInfo>
);

export default UsesVideo;

export {
  PassengerConfig,
  CargoConfig,
  SARConfig,
  DefenseConfig,
  // passengerVideoSrc,
  // hemsSarVideoSrc,
  // defenseVideoSrc,
  // cargoVideoSrc,
};
